import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card'
import { Award, AlertTriangle } from 'lucide-react'

interface AlertsSectionProps {
  currentPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  } | null
  previousPeriodData: {
    ridesCompleted: number
    commission: number
    rating: string
  }
  subscription?: {
    start?: string
    end?: string
    value?: number
    restantDays?: number
  } | null
}

export default function AlertsSection({
  currentPeriodData,
  previousPeriodData,
  subscription,
}: AlertsSectionProps) {
  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return '0.00'
    const growth = ((current - previous) / previous) * 100
    return growth.toFixed(2)
  }

  const getSubscriptionAlert = () => {
    if (!subscription?.restantDays) {
      return {
        show: false,
        message: '',
        days: 0,
      }
    }

    return {
      show: true,
      message:
        subscription.restantDays === 1
          ? 'Assinatura vence amanhã!'
          : `Faltam ${subscription.restantDays} dias para a assinatura acabar`,
      days: subscription.restantDays,
    }
  }

  const subscriptionAlert = getSubscriptionAlert()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Alertas e Observações</CardTitle>
        <CardDescription className="text-sm">Pontos de atenção</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {subscriptionAlert.show && (
            <div
              className={`flex items-start gap-2 p-3 rounded-lg border ${
                subscriptionAlert.days <= 7
                  ? 'bg-red-50 border-red-200'
                  : subscriptionAlert.days <= 15
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-blue-50 border-blue-200'
              }`}
            >
              <AlertTriangle
                className={`h-5 w-5 mt-0.5 ${
                  subscriptionAlert.days <= 7
                    ? 'text-red-600'
                    : subscriptionAlert.days <= 15
                      ? 'text-yellow-600'
                      : 'text-blue-600'
                }`}
              />
              <div>
                <p
                  className={`text-sm font-medium ${
                    subscriptionAlert.days <= 7
                      ? 'text-red-800'
                      : subscriptionAlert.days <= 15
                        ? 'text-yellow-800'
                        : 'text-blue-800'
                  }`}
                >
                  {subscriptionAlert.days <= 7
                    ? 'Assinatura expirando em breve'
                    : 'Assinatura vencendo'}
                </p>
                <p
                  className={`text-xs ${
                    subscriptionAlert.days <= 7
                      ? 'text-red-700'
                      : subscriptionAlert.days <= 15
                        ? 'text-yellow-700'
                        : 'text-blue-700'
                  }`}
                >
                  {subscriptionAlert.message}
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <Award className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-700">
                {Number(currentPeriodData?.rating || 0) > 4.8
                  ? 'Excelente avaliação'
                  : Number(currentPeriodData?.rating || 0) >= 4.5
                    ? 'Avaliação Boa'
                    : 'Avaliação Ruim'}
              </p>
              <p className="text-xs text-green-700">
                {Number(currentPeriodData?.rating || 0) > 4.8
                  ? 'Mantendo média acima de 4.8 estrelas'
                  : Number(currentPeriodData?.rating || 0) >= 4.5
                    ? 'Média entre 4.5 e 4.8 estrelas'
                    : 'Média abaixo de 4.5 estrelas, considerar melhorias'}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            {parseFloat(
              calculateGrowth(
                currentPeriodData?.ridesCompleted || 0,
                previousPeriodData.ridesCompleted
              )
            ) >= 0 ? (
              <>
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 10l7-7m0 0l7 7m-7-7v18"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Crescimento consistente
                  </p>
                  <p className="text-xs text-blue-700">
                    Aumento de{' '}
                    {calculateGrowth(
                      currentPeriodData?.ridesCompleted || 0,
                      previousPeriodData.ridesCompleted
                    )}
                    % nas corridas
                  </p>
                </div>
              </>
            ) : (
              <>
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Queda no desempenho
                  </p>
                  <p className="text-xs text-blue-700">
                    Redução de{' '}
                    {Math.abs(
                      parseFloat(
                        calculateGrowth(
                          currentPeriodData?.ridesCompleted || 0,
                          previousPeriodData.ridesCompleted
                        )
                      )
                    )}
                    % nas corridas
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
